import { AppSidebar } from "@/components/ui/app-sidebar"
import { SiteHeader } from "@/components/ui/site-header"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { ROUTES } from "@/constants/routes"
import { checkRoleRequirement } from "@/utils/roleUtils"
import { RoleRequirements } from "@/utils/roleUtils"
import { useAppSelector } from "@/store/hooks"
import { Outlet, useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "@/store/hooks"
import { UserRole } from "@/utils/roleUtils"
import { NAVIGATION_CONFIG } from "@/constants/routes"
import { logoutUser } from "@/store/thunks/authThunks"
import { useMemo, useCallback, useEffect } from "react"
import GlossaryModal from '@/components/common/GlossaryModal'
import { openGlossaryModal } from '@/store/slices/glossarySlice'

export default function Page() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { user, isAuthenticated } = authState

  // Keyboard shortcut for glossary (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isAuthenticated && (event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault()
        dispatch(openGlossaryModal())
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isAuthenticated, dispatch])

  const handleGlossaryClick = () => {
    dispatch(openGlossaryModal())
  }

  // Memoize the getRoleDisplayName function
  const getRoleDisplayName = useCallback((role: string): string => {
    const roleNames: Record<string, string> = {
      hr_admin: 'HR Admin',
      general_manager: 'Operations',
      system_admin: 'System Admin'
    }
    return roleNames[role] || role
  }, [])

  // Memoize navigation items calculation
  const navigationItems = useMemo(() => {
    if (!user) return []

    const userRole = user.role as UserRole
    const items = []

    // HR Agent - accessible to system admin and hr admin
    if (checkRoleRequirement(userRole, RoleRequirements.adminRoles)) {
      items.push({
        name: NAVIGATION_CONFIG.HR_AGENT.name,
        href: NAVIGATION_CONFIG.HR_AGENT.path,
        current: location.pathname === NAVIGATION_CONFIG.HR_AGENT.path
      })
    }

    // Operations Agent - only accessible to system admin
    if (checkRoleRequirement(userRole, RoleRequirements.systemAdminOnly)) {
      items.push({
        name: NAVIGATION_CONFIG.OPS_AGENT.name,
        href: NAVIGATION_CONFIG.OPS_AGENT.path,
        current: location.pathname === NAVIGATION_CONFIG.OPS_AGENT.path
      })
    }

    // Settings - only system admin
    if (checkRoleRequirement(userRole, RoleRequirements.systemAdminOnly)) {
      items.push({
        name: NAVIGATION_CONFIG.SETTINGS.name,
        href: NAVIGATION_CONFIG.SETTINGS.path,
        current: location.pathname === NAVIGATION_CONFIG.SETTINGS.path
      })
      
      // Glossary Management - only system admin
      items.push({
        name: NAVIGATION_CONFIG.GLOSSARY_MANAGEMENT.name,
        href: NAVIGATION_CONFIG.GLOSSARY_MANAGEMENT.path,
        current: location.pathname === NAVIGATION_CONFIG.GLOSSARY_MANAGEMENT.path
      })
    }

    return items
  }, [user, location.pathname])

  // Memoize the logout handler
  const handleLogout = useCallback(async () => {
    try {
      // Dispatch the logout thunk
      await dispatch(logoutUser()).unwrap()
      // Navigate to login page after successful logout
      navigate(ROUTES.LOGIN, { replace: true })
    } catch (error) {
      // Even if server logout fails, we'll still be logged out locally
      // and redirected due to the auth slice handling
      console.warn('Logout error:', error)
      navigate(ROUTES.LOGIN, { replace: true })
    }
  }, [dispatch, navigate])

  // Memoize the sign in handler
  const handleSignIn = useCallback(() => {
    navigate(ROUTES.LOGIN)
  }, [navigate])

  return (
    <div className="[--header-height:calc(theme(spacing.14))]">
      <SidebarProvider className="flex flex-col">
        <SiteHeader />
        <div className="flex flex-1">
          <AppSidebar />
          <SidebarInset className={(user?.role as string) === 'unassigned' ? 'ml-0' : ''}>
            <Outlet />
          </SidebarInset>
        </div>
      </SidebarProvider>
      
      {/* Global Glossary Modal */}
      <GlossaryModal />
    </div>
  )
}
